"use client";

import { useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from "@/components/ui/table";
import { PublicTag } from "@classview/shared-types";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { RecordingTagsRowActions } from "./recording-tags-row-actions";

interface RecordingTagsTableProps {
  data: PublicTag[];
  organisationId: string;
  isLoading?: boolean;
}

export function RecordingTagsTable({
  data,
  organisationId,
  isLoading,
}: RecordingTagsTableProps) {
  const columnHelper = createColumnHelper<PublicTag>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => (
          <>
            <span className="block truncate">{info.getValue()}</span>
          </>
        ),
        header: () => <span>Name</span>,
      }),
      columnHelper.display({
        id: "actions",
        cell: ({ row }) => (
          <RecordingTagsRowActions
            tagId={row.original.id}
            organisationId={organisationId}
          />
        ),
        header: () => <span>Actions</span>,
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
    defaultColumn: {
      size: 999,
    },
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="hover:bg-transparent" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize() === 999 ? "auto" : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TablePagination table={table} isLoading={isLoading} />
      </TableFooter>
    </Table>
  );
}
